/**
 * Fixed day numbers ("R.D.", rata die) for the calendar tools.
 *
 * Reingold and Dershowitz, *Calendrical Calculations: The Ultimate Edition* (Cambridge
 * University Press, 2018), count days from R.D. 1 = Monday 1 January 1 CE in the proleptic
 * Gregorian calendar. Every calendar in that book is a pair of functions between a date and
 * this one number, which is what makes conversions between any two of them a two-step
 * route through R.D.
 *
 * The rest of the site counts days since 1970-01-01 (`week-number.ts`), so this file is the
 * bridge: `rd = days + 719 163`. Nothing here reads a clock or a time zone.
 */

import {
  type CivilDate,
  civilFromDays,
  daysFromCivil,
} from "@/lib/tool-kit/converters/week-number";

/** R.D. of 1970-01-01 (the Unix epoch). */
export const RD_OF_UNIX_EPOCH = 719_163;
/** R.D. of 31 December 9999 (Gregorian) — the last day a date field accepts. */
export const RD_LAST_GREGORIAN = daysFromCivil(9999, 12, 31) + RD_OF_UNIX_EPOCH;

/** `a mod b` with the sign of `b`, as the book defines it (never negative for b > 0). */
export function mod(a: number, b: number): number {
  return a - b * Math.floor(a / b);
}

export function rdFromDays(days: number): number {
  return days + RD_OF_UNIX_EPOCH;
}

export function daysFromRd(rd: number): number {
  return rd - RD_OF_UNIX_EPOCH;
}

/** R.D. of a proleptic Gregorian date. */
export function rdFromGregorian(
  year: number,
  month: number,
  day: number
): number {
  return daysFromCivil(year, month, day) + RD_OF_UNIX_EPOCH;
}

/** The proleptic Gregorian date of an R.D. */
export function gregorianFromRd(rd: number): CivilDate {
  return civilFromDays(rd - RD_OF_UNIX_EPOCH);
}

/** 0 = Sunday … 6 = Saturday. R.D. 0 was a Sunday. */
export function weekdayFromRd(rd: number): number {
  return mod(rd, 7);
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `YYYY-MM-DD` as an R.D., or null when it is not a real proleptic Gregorian date between
 * 1 January 1 CE and 31 December 9999.
 */
export function parseGregorianIso(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) {
    return null;
  }
  const rd = rdFromGregorian(year, month, day);
  const back = gregorianFromRd(rd);
  return back.month === month && back.day === day ? rd : null;
}

/** An R.D. as `YYYY-MM-DD`. */
export function isoFromRd(rd: number): string {
  const { year, month, day } = gregorianFromRd(rd);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Midnight UTC of an R.D., for `Intl.DateTimeFormat` with `timeZone: "UTC"`. */
export function utcDateFromRd(rd: number): Date {
  return new Date((rd - RD_OF_UNIX_EPOCH) * 86_400_000);
}
